import { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, IconButton,
  CircularProgress, Chip, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, TextField, 
  FormControl, InputLabel, Select, MenuItem, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  Refresh, LocationOn, Timer, Navigation,
} from '@mui/icons-material';
import { InvokeApi, apiList } from '../services/ApiService';
import { Config } from '../config/Config';
import VehiclePathMap from './VehiclePathMap';

/**
 * Vehicle Path Details Dashboard
 * Shows complete journey history with route visualization
 * 
 * Features:
 * - Daily path tracking (start/stop times, locations)
 * - Distance calculation (Haversine formula)
 * - Speed analysis (max, avg, current)
 * - Route polyline on map
 * - Monthly statistics (daily breakdown)
 * - Journey segmentation (continuous movement)
 */

const VehiclePathDetails = () => {
  const { vehicleId: urlVehicleId } = useParams();
  const [searchParams] = useSearchParams();
  const urlDate = searchParams.get('date');

  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState(urlVehicleId ? parseInt(urlVehicleId) : null);
  const [viewMode, setViewMode] = useState('daily'); // daily or monthly
  const [selectedDate, setSelectedDate] = useState(urlDate || new Date().toISOString().split('T')[0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  
  const [dailyData, setDailyData] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState('map'); // Default to map view

  // ── Update selected vehicle when URL changes
  useEffect(() => {
    if (urlVehicleId) {
      setSelectedVehicleId(parseInt(urlVehicleId));
    }
  }, [urlVehicleId]);

  // ── Update selected date when URL changes and show map
  useEffect(() => {
    if (urlDate) {
      setSelectedDate(urlDate);
      setViewMode('daily'); // Force daily view when date is provided
      setTabValue('map'); // Show map tab when navigating from Fleet Report
    }
  }, [urlDate]);

  // ── Fetch vehicles list
  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        // Try to get vehicles with GPS data first
        const res = await InvokeApi(Config.base_url + '/device/locations/active', {});
        if (res?.code === '200' && res.locations && res.locations.length > 0) {
          setVehicles(res.locations || []);
          // Only set first vehicle if no URL vehicle ID is provided
          if (!urlVehicleId && res.locations.length > 0) {
            setSelectedVehicleId(res.locations[0].vehicle_id);
          }
        } else {
          // If no GPS data, get all vehicles from admin vehicles endpoint
          const vehiclesRes = await InvokeApi(Config.base_url + '/admin/vehicles', { page: 1, per_page: 500 });
          if (vehiclesRes?.code === '200' && vehiclesRes.vehicles) {
            const vehicleList = vehiclesRes.vehicles.map(v => ({
              vehicle_id: v.id,
              vehicle_number: v.vehicle_number,
              model: v.model,
              color: v.color,
              status: v.status,
            }));
            setVehicles(vehicleList);
            // Only set first vehicle if no URL vehicle ID is provided
            if (!urlVehicleId && vehicleList.length > 0) {
              setSelectedVehicleId(vehicleList[0].vehicle_id);
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch vehicles:', err);
        // Fallback: show empty state
        setVehicles([]);
      }
    };
    fetchVehicles();
  }, [urlVehicleId]);

  // ── Fetch daily path data — shared endpoint, same as VehicleDetail + FleetMovementReport
  const fetchDailyPath = useCallback(async () => {
    if (!selectedVehicleId) return;
    setLoading(true);
    try {
      const res = await InvokeApi(Config.base_url + apiList.getVehiclePathHistory, {
        vehicle_id: selectedVehicleId,
        date: selectedDate,
      });
      if (res?.code === '200' && Array.isArray(res.points)) {
        // Compute distance + journey count from sampled points (Haversine, skip jumps > 2 km)
        let totalKm = 0;
        const pts = res.points;
        for (let i = 1; i < pts.length; i++) {
          const R = 6371;
          const dLat = (pts[i].latitude  - pts[i-1].latitude)  * Math.PI / 180;
          const dLon = (pts[i].longitude - pts[i-1].longitude) * Math.PI / 180;
          const a = Math.sin(dLat/2)**2 + Math.cos(pts[i-1].latitude * Math.PI/180)
                  * Math.cos(pts[i].latitude * Math.PI/180) * Math.sin(dLon/2)**2;
          const km = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
          if (km < 2) totalKm += km;
        }
        setDailyData({
          total_distance_km: Math.round(totalKm * 100) / 100,
          total_time_minutes: 0,
          max_speed_kmh: pts.length ? Math.max(...pts.map(p => p.speed || 0)) : 0,
          journey_count: pts.length > 0 ? 1 : 0,
          journeys: [],          // path-history doesn't return journey segments
          all_coordinates: pts.map(p => ({ lat: p.latitude, lng: p.longitude, speed: p.speed, time: p.timestamp })),
          points: pts,
        });
      } else {
        setDailyData(null);
      }
    } catch (err) {
      console.error('Failed to fetch daily path:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedVehicleId, selectedDate]);

  // ── Fetch monthly statistics — shared endpoint, same as VehicleDetail
  const fetchMonthlyStats = useCallback(async () => {
    if (!selectedVehicleId) return;
    setLoading(true);
    try {
      const [yr, mo] = selectedMonth.split('-');
      const res = await InvokeApi(Config.base_url + apiList.getVehicleSummary, {
        vehicle_id: selectedVehicleId,
        month: parseInt(mo),
        year:  parseInt(yr),
      });
      if (res?.code === '200' && res.summary) {
        const s = res.summary;
        setMonthlyData({
          total_distance_km:     s.total_km,
          days_active:           s.active_days,
          avg_daily_distance_km: s.active_days > 0 ? Math.round((s.total_km / s.active_days) * 100) / 100 : 0,
          month:                 s.period?.month_name || selectedMonth,
          // Expose as daily_breakdown so render can use it
          daily_breakdown:       s.daily_breakdown,  // [{date, distance_km}]
        });
      } else {
        setMonthlyData(null);
      }
    } catch (err) {
      console.error('Failed to fetch monthly stats:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedVehicleId, selectedMonth]);

  // ── Auto-fetch when view or selection changes
  useEffect(() => {
    if (viewMode === 'daily') {
      fetchDailyPath();
    } else {
      fetchMonthlyStats();
    }
  }, [viewMode, selectedVehicleId, selectedDate, selectedMonth, fetchDailyPath, fetchMonthlyStats]);

  // ── Format time
  const formatTime = (timestamp) => {
    if (!timestamp) return '—';
    return new Date(timestamp).toLocaleTimeString();
  };

  // ── Get color based on speed
  const getSpeedColor = (speed) => {
    if (speed > 40) return '#e53935'; // red - fast
    if (speed > 20) return '#ff9800'; // orange - medium
    return '#43a047'; // green - slow
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">
            <Navigation sx={{ mr: 1, verticalAlign: 'middle' }} />
            Vehicle Path Tracking
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Detailed journey history with route visualization
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Select Vehicle</InputLabel>
            <Select
              value={selectedVehicleId || ''}
              label="Select Vehicle"
              onChange={(e) => setSelectedVehicleId(e.target.value)}
            >
              {vehicles.map((v) => (
                <MenuItem key={v.vehicle_id} value={v.vehicle_id}>
                  {v.vehicle_number} - {v.model}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Refresh Data">
            <IconButton onClick={() => viewMode === 'daily' ? fetchDailyPath() : fetchMonthlyStats()} disabled={loading} sx={{ color: '#1a237e' }}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* View Mode Toggle */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <ToggleButtonGroup value={viewMode} exclusive onChange={(e, newMode) => newMode && setViewMode(newMode)}>
          <ToggleButton value="daily" sx={{ color: '#1a237e', '&.Mui-selected': { backgroundColor: '#1a237e', color: '#fff' } }}>
            📅 Daily
          </ToggleButton>
          <ToggleButton value="monthly" sx={{ color: '#1a237e', '&.Mui-selected': { backgroundColor: '#1a237e', color: '#fff' } }}>
            📊 Monthly
          </ToggleButton>
        </ToggleButtonGroup>

        {viewMode === 'daily' && (
          <TextField type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} size="small" />
        )}
        {viewMode === 'monthly' && (
          <TextField type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} size="small" />
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress sx={{ color: '#1a237e' }} />
        </Box>
      ) : viewMode === 'daily' && dailyData ? (
        <>
          {/* Daily Summary Stats */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Total Distance', value: `${dailyData.total_distance_km} km`, icon: '📍', color: '#1a237e' },
              { label: 'Journey Time', value: `${dailyData.total_time_minutes} min`, icon: '⏱️', color: '#ff9800' },
              { label: 'Max Speed', value: `${dailyData.max_speed_kmh} km/h`, icon: '🚀', color: '#e53935' },
              { label: 'Trips', value: dailyData.journey_count, icon: '🛣️', color: '#43a047' },
            ].map((stat, i) => (
              <Grid item xs={6} sm={3} key={i}>
                <Card sx={{ borderRadius: 2, background: `linear-gradient(135deg, ${stat.color}20, ${stat.color}10)`, border: `2px solid ${stat.color}` }}>
                  <CardContent sx={{ py: 1.5, px: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: 11 }}>
                      {stat.icon} {stat.label}
                    </Typography>
                    <Typography variant="h6" fontWeight={700} color={stat.color}>
                      {stat.value}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Journeys Table */}
          <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', mb: 3 }}>
            {/* Simple Tab Buttons */}
            <Box sx={{ display: 'flex', borderBottom: 1, borderColor: 'divider', px: 2, flexWrap: 'wrap' }}>
              <Button
                onClick={() => setTabValue('journeys')}
                sx={{
                  py: 1.5,
                  px: 2,
                  borderBottom: tabValue === 'journeys' ? '3px solid #1a237e' : 'none',
                  color: tabValue === 'journeys' ? '#1a237e' : '#999',
                  fontWeight: tabValue === 'journeys' ? 700 : 400,
                }}
              >
                📋 Journeys ({dailyData.journey_count})
              </Button>
              <Button
                onClick={() => setTabValue('timeline')}
                sx={{
                  py: 1.5,
                  px: 2,
                  borderBottom: tabValue === 'timeline' ? '3px solid #1a237e' : 'none',
                  color: tabValue === 'timeline' ? '#1a237e' : '#999',
                  fontWeight: tabValue === 'timeline' ? 700 : 400,
                }}
              >
                ⏱️ Timeline
              </Button>
              <Button
                onClick={() => setTabValue('map')}
                sx={{
                  py: 1.5,
                  px: 2,
                  borderBottom: tabValue === 'map' ? '3px solid #1a237e' : 'none',
                  color: tabValue === 'map' ? '#1a237e' : '#999',
                  fontWeight: tabValue === 'map' ? 700 : 400,
                }}
              >
                🗺️ Route Map
              </Button>
            </Box>

            {/* Tab Content */}
            {tabValue === 'journeys' ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ background: '#f5f6fa' }}>
                        {['Start Time', 'Start Location', 'End Time', 'End Location', 'Distance', 'Duration', 'Speed (avg/max)'].map((h) => (
                          <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12, color: '#1a237e' }}>
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dailyData.journeys.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                            No journeys recorded for this date
                          </TableCell>
                        </TableRow>
                      ) : (
                        dailyData.journeys.map((journey, i) => (
                          <TableRow key={i} hover>
                            <TableCell fontSize={11}>{formatTime(journey.start_time)}</TableCell>
                            <TableCell fontSize={11} sx={{ fontFamily: 'monospace' }}>
                              {journey.start_lat.toFixed(4)}, {journey.start_lon.toFixed(4)}
                            </TableCell>
                            <TableCell fontSize={11}>{formatTime(journey.end_time)}</TableCell>
                            <TableCell fontSize={11} sx={{ fontFamily: 'monospace' }}>
                              {journey.end_lat.toFixed(4)}, {journey.end_lon.toFixed(4)}
                            </TableCell>
                            <TableCell>
                              <Chip label={`${journey.distance_km} km`} size="small" icon={<LocationOn sx={{ fontSize: 14 }} />} />
                            </TableCell>
                            <TableCell>
                              <Chip label={`${journey.duration_minutes} min`} size="small" icon={<Timer sx={{ fontSize: 14 }} />} variant="outlined" />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Chip
                                  label={`${journey.avg_speed_kmh} km/h`}
                                  size="small"
                                  sx={{ background: getSpeedColor(journey.avg_speed_kmh), color: '#fff' }}
                                />
                                <Chip
                                  label={`${journey.max_speed_kmh} km/h`}
                                  size="small"
                                  variant="outlined"
                                />
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
            ) : tabValue === 'timeline' ? (
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2, color: '#1a237e' }}>
                  Journey Timeline - {selectedDate}
                </Typography>
                <Box sx={{ display: 'grid', gap: 2 }}>
                  {dailyData.journeys.length === 0 ? (
                    <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                      No journeys recorded for this date
                    </Typography>
                  ) : (
                    dailyData.journeys.map((journey, idx) => (
                      <Card key={idx} sx={{ borderRadius: 2, background: '#f5f6fa', border: '2px solid #e0e0e0' }}>
                        <CardContent>
                          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                            {/* Journey Header */}
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                                Journey #{idx + 1}
                              </Typography>
                              <Typography variant="h6" fontWeight={700} color="#1a237e">
                                {journey.distance_km} km in {journey.duration_minutes} min
                              </Typography>
                            </Box>
                            <Box>
                              <Chip label={`Avg: ${journey.avg_speed_kmh} km/h`} size="small" sx={{ mr: 1, mb: 1 }} />
                              <Chip label={`Max: ${journey.max_speed_kmh} km/h`} size="small" color="error" />
                            </Box>
                          </Box>

                          {/* Timeline */}
                          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 2, alignItems: 'start' }}>
                            {/* Start */}
                            <Box sx={{ textAlign: 'center', pt: 0.5 }}>
                              <Box sx={{ width: 16, height: 16, borderRadius: '50%', background: '#4CAF50', mx: 'auto', mb: 1 }} />
                              <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 700 }}>START</Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" fontWeight={700}>
                                {new Date(journey.start_time).toLocaleTimeString()}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace', fontSize: 9 }}>
                                {journey.start_lat.toFixed(6)}, {journey.start_lon.toFixed(6)}
                              </Typography>
                            </Box>

                            {/* Middle Line */}
                            <Box sx={{ textAlign: 'center', pt: 2, pb: 2 }}>
                              <Box sx={{ width: 2, height: 40, background: '#1a237e', mx: 'auto' }} />
                            </Box>
                            <Box sx={{ pt: 2, pb: 2, borderLeft: '2px dashed #ccc', pl: 2 }}>
                              <Typography variant="caption" color="text.secondary">
                                Moving · {journey.avg_speed_kmh} km/h avg
                              </Typography>
                            </Box>

                            {/* End */}
                            <Box sx={{ textAlign: 'center', pb: 0.5 }}>
                              <Box sx={{ width: 16, height: 16, borderRadius: '50%', background: '#f44336', mx: 'auto', mb: 1 }} />
                              <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 700 }}>END</Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" fontWeight={700}>
                                {new Date(journey.end_time).toLocaleTimeString()}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace', fontSize: 9 }}>
                                {journey.end_lat.toFixed(6)}, {journey.end_lon.toFixed(6)}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </Box>
              </Box>
            ) : (
              <VehiclePathMap vehicleId={selectedVehicleId} date={selectedDate} onClose={() => setMapOpen(false)} />
            )}
          </Card>
        </>
      ) : viewMode === 'monthly' && monthlyData ? (
        <>
          {/* Monthly Summary Stats */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Total Distance', value: `${monthlyData.total_distance_km} km`, icon: '📍', color: '#1a237e' },
              { label: 'Active Days', value: monthlyData.days_active, icon: '📅', color: '#ff9800' },
              { label: 'Avg Daily', value: `${monthlyData.avg_daily_distance_km} km`, icon: '📊', color: '#43a047' },
            ].map((stat, i) => (
              <Grid item xs={6} sm={4} key={i}>
                <Card sx={{ borderRadius: 2, background: `linear-gradient(135deg, ${stat.color}20, ${stat.color}10)`, border: `2px solid ${stat.color}` }}>
                  <CardContent sx={{ py: 1.5, px: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: 11 }}>
                      {stat.icon} {stat.label}
                    </Typography>
                    <Typography variant="h6" fontWeight={700} color={stat.color}>
                      {stat.value}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Daily Breakdown Table */}
          <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: '#1a237e' }}>
                Daily Breakdown - {monthlyData.month}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ background: '#f5f6fa' }}>
                      {['Date', 'Distance (km)'].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12, color: '#1a237e' }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(!monthlyData.daily_breakdown || monthlyData.daily_breakdown.length === 0) ? (
                      <TableRow>
                        <TableCell colSpan={2} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                          No data for this month
                        </TableCell>
                      </TableRow>
                    ) : (
                      monthlyData.daily_breakdown.map((day, i) => (
                        <TableRow key={i} hover>
                          <TableCell sx={{ fontSize: 11, fontWeight: 600 }}>{day.date}</TableCell>
                          <TableCell>
                            <Chip label={`${day.distance_km} km`} size="small" icon={<LocationOn sx={{ fontSize: 14 }} />} />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <Typography color="text.secondary">No data available. Please select a vehicle and date.</Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default VehiclePathDetails;
